import type * as type from "@vTypes";
import { Ban, Check, Eraser, MessageCircle, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getReadableShiftCodeTextColor } from "@/utils/fontSizeUtils";
import CommentInput from "./CommentInput";

interface SelectCalendarEntry {
  type: string;
  comment: string | null;
}

interface Type_SelectCalendar {
  [date: string]: SelectCalendarEntry;
}

interface CardDateProps {
  day: number;
  holiday: { weekend: number; isHol: boolean };
  dateStr: string;
  availableShifts: type.Type_availableShifts[];
  shift: { type: string; comment: string | null } | null;
  setSelectedCalendarData: (
    value:
      Type_SelectCalendar | ((prev: Type_SelectCalendar) => Type_SelectCalendar)
  ) => void;
  selectedShiftType: string;
  submittedStatus: string;
  isExistWanted: boolean;
  /** 사유 입력 기능 활성화 여부 */
  enableCommentInput?: boolean;
  /** 현재 값 / 최대 값 표시용 */
  currentCount?: number;
  maxCount?: number;
  avoidShiftCode?: string;
  avoidComment?: string | null;
  calendarDisplay?: "all" | "wanted" | "avoid";
  activeIntent?: "wanted" | "avoid" | null;
  onAvoidShiftRemove?: (dateStr: string) => void;
  onDateClick?: (
    dateStr: string,
    intent: "wanted" | "avoid",
    comment?: string,
    shiftId?: string
  ) => void;
  onMissingShiftSelection?: () => void;
  onLockedDateClick?: (dateStr: string) => void;
}

const WEEKLY_OFF_COMMENT = "주휴";
const WEEKDAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"] as const;

function getShiftTintColor(color: string | undefined) {
  const hex = color?.replace("#", "");
  if (!hex || !/^[\dA-Fa-f]{6}$/.test(hex)) return color;

  return `#${hex}18`;
}

function Card_date({
  day,
  holiday,
  dateStr,
  availableShifts,
  shift,
  setSelectedCalendarData,
  selectedShiftType,
  submittedStatus,
  isExistWanted,
  enableCommentInput = false,
  currentCount = 0,
  maxCount,
  avoidShiftCode,
  avoidComment,
  calendarDisplay = "all",
  activeIntent = null,
  onAvoidShiftRemove,
  onDateClick,
  onMissingShiftSelection,
  onLockedDateClick,
}: CardDateProps) {
  // 원티드 작성 > 사유작성을 위한 상태들
  const [isIntentPopoverOpen, setIsIntentPopoverOpen] = useState(false);
  const currentComment = avoidShiftCode ? avoidComment : shift?.comment;
  const [commentValue, setCommentValue] = useState(currentComment || "");

  const isWeeklyOff = shift?.comment === WEEKLY_OFF_COMMENT;
  const isDateSelectionDisabled = submittedStatus !== "requested";
  const hasAvoidShiftMarker = Boolean(avoidShiftCode);
  const hasPreferredShift = Boolean(shift?.type && !isWeeklyOff);
  const isFilterInactive =
    (calendarDisplay === "wanted" && hasAvoidShiftMarker) ||
    (calendarDisplay === "avoid" && hasPreferredShift);

  const shiftDefinition = shift
    ? availableShifts.find((item) => item.shift_id === shift.type)
    : undefined;
  const selectedShiftDefinition = selectedShiftType
    ? availableShifts.find((item) => item.shift_id === selectedShiftType)
    : undefined;
  const bgColor = shift?.type === "주" ? "#D6D6DE" : shiftDefinition?.color;
  const avoidShiftColor = hasAvoidShiftMarker
    ? availableShifts.find((item) => item.shift_id === avoidShiftCode)?.color
    : undefined;
  const avoidShiftDefinition = hasAvoidShiftMarker
    ? availableShifts.find((item) => item.shift_id === avoidShiftCode)
    : undefined;
  const shiftColor = hasAvoidShiftMarker ? avoidShiftColor : bgColor;
  const shiftTintColor = getShiftTintColor(
    hasAvoidShiftMarker ? avoidShiftColor : bgColor
  );

  const hasSavedCommentValue = useMemo(() => {
    return !!currentComment && !isWeeklyOff;
  }, [currentComment, isWeeklyOff]);

  useEffect(() => {
    setCommentValue(currentComment || "");
  }, [currentComment]);

  // 사유 저장
  const handleSaveComment = () => {
    if (hasAvoidShiftMarker && avoidShiftCode) {
      onDateClick?.(dateStr, "avoid", commentValue, avoidShiftCode);
      return;
    }

    if (!shift?.type) return;

    setSelectedCalendarData((prev) => ({
      ...prev,
      [dateStr]: {
        ...prev[dateStr],
        comment: commentValue || null,
      },
    }));
  };

  // 사유 삭제
  const handleDelete = () => {
    if (hasAvoidShiftMarker && avoidShiftCode) {
      setCommentValue("");
      onDateClick?.(dateStr, "avoid", "", avoidShiftCode);
      return;
    }

    if (!shift?.type) return;

    setCommentValue("");
    setSelectedCalendarData((prev) => ({
      ...prev,
      [dateStr]: {
        ...prev[dateStr],
        comment: null,
      },
    }));
  };

  // 주휴는 회색 bg로 보여주고 주휴로 보여주기 위해 프론트에서
  const shiftText = shift?.type === "주" ? "주휴" : shift?.type;
  const chipCode = hasAvoidShiftMarker ? avoidShiftCode : shiftText;
  const chipLabel = hasAvoidShiftMarker
    ? avoidShiftDefinition?.name || avoidShiftCode
    : shiftDefinition?.name || shiftText;
  const hasCalendarEntry = Boolean(chipCode);
  const shouldApplyActiveIntentImmediately = Boolean(
    activeIntent &&
    selectedShiftType &&
    !hasCalendarEntry &&
    !isDateSelectionDisabled &&
    !isWeeklyOff
  );
  const selectedShiftColor = selectedShiftDefinition?.color;
  const currentIntent = hasAvoidShiftMarker
    ? "avoid"
    : hasPreferredShift
      ? "wanted"
      : null;

  const handleIntentPopoverOpenChange = (open: boolean) => {
    if (isDateSelectionDisabled || isWeeklyOff) {
      setIsIntentPopoverOpen(false);
      return;
    }

    if (open && !selectedShiftType) {
      if (currentIntent) {
        setIsIntentPopoverOpen(true);
      } else {
        onMissingShiftSelection?.();
      }
      return;
    }

    if (open && shouldApplyActiveIntentImmediately) {
      setIsIntentPopoverOpen(false);
      return;
    }

    setIsIntentPopoverOpen(open);
  };

  const handleClearDate = () => {
    if (hasAvoidShiftMarker) {
      onAvoidShiftRemove?.(dateStr);
    }

    if (shift && !isWeeklyOff) {
      setSelectedCalendarData((prev) => {
        const next = { ...prev };
        delete next[dateStr];
        return next;
      });
    }

    setIsIntentPopoverOpen(false);
  };

  const handleApplyIntent = (intent: "wanted" | "avoid") => {
    onDateClick?.(
      dateStr,
      intent,
      commentValue,
      hasCalendarEntry ? chipCode : selectedShiftType
    );
  };

  const popoverShiftCode = hasCalendarEntry
    ? chipCode || ""
    : selectedShiftType;
  const popoverShiftDefinition = hasCalendarEntry
    ? (avoidShiftDefinition ?? shiftDefinition)
    : selectedShiftDefinition;
  const popoverShiftColor = hasCalendarEntry ? shiftColor : selectedShiftColor;
  const popoverShiftTextColor =
    getReadableShiftCodeTextColor(popoverShiftColor);
  const isPreviewingSelectedShift =
    isIntentPopoverOpen && !hasCalendarEntry && Boolean(selectedShiftType);
  const displayShiftCode = isPreviewingSelectedShift
    ? selectedShiftType
    : chipCode;
  const displayShiftLabel = isPreviewingSelectedShift
    ? selectedShiftDefinition?.name || selectedShiftType
    : chipLabel;
  const displayShiftColor = isPreviewingSelectedShift
    ? selectedShiftColor
    : shiftColor;
  const displayShiftTintColor = isPreviewingSelectedShift
    ? getShiftTintColor(selectedShiftColor)
    : shiftTintColor;
  const displayShiftTextColor =
    getReadableShiftCodeTextColor(displayShiftColor);
  const hasDisplayEntry = Boolean(displayShiftCode);
  const showRequestStatus =
    hasCalendarEntry && !isWeeklyOff && !isPreviewingSelectedShift;
  const dateContextLabel = `${Number(dateStr.slice(5, 7))}월 ${day}일 (${WEEKDAY_LABELS[holiday.weekend]})`;

  return (
    <div className="wanted-calendar-cell">
      <div
        className={`w_selectCalendar_date group relative font-pretendard font-medium !text-[15px] text-text-sub
                ${holiday.weekend === 6 ? "!text-text-sat" : holiday.weekend === 0 ? "!text-text-sun" : ""}
                ${holiday.isHol && "!text-text-holiday"}
                ${(submittedStatus === "closed" || isWeeklyOff) && "!cursor-not-allowed"}
                `}
      >
        <div className="calendar-date !p-1 !top-[1px] !left-[1px]">{day}</div>
        {isExistWanted ? (
          <>
            <Popover
              open={isIntentPopoverOpen}
              onOpenChange={handleIntentPopoverOpenChange}
            >
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={`w_selectCalendar_shiftButton touch-manipulation transition-opacity focus-visible:ring-2 focus-visible:ring-sig focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${isDateSelectionDisabled ? "!shadow-none !hover:shadow-none" : ""} ${isFilterInactive ? "opacity-20" : ""}`}
                  aria-label={
                    shouldApplyActiveIntentImmediately
                      ? `${dateStr}에 ${selectedShiftType} ${activeIntent === "avoid" ? "피하고 싶은" : "희망"} 근무를 바로 적용`
                      : isPreviewingSelectedShift
                        ? `${dateStr}에 ${selectedShiftType} 근무를 적용할 준비됨`
                        : hasAvoidShiftMarker
                          ? `${dateStr} 피하고 싶은 ${avoidShiftCode} 근무 표시`
                          : selectedShiftType
                            ? `${dateStr}에 ${selectedShiftType} 근무 신청 유형 선택`
                            : `${dateStr} 근무코드를 선택한 뒤 입력할 수 있습니다.`
                  }
                  title={
                    isWeeklyOff
                      ? "확정된 일정은 변경할 수 없습니다."
                      : !selectedShiftType && submittedStatus === "requested"
                        ? "근무코드를 먼저 선택하세요"
                        : undefined
                  }
                  disabled={isDateSelectionDisabled}
                  onClick={() => {
                    if (isWeeklyOff) {
                      onLockedDateClick?.(dateStr);
                      return;
                    }

                    if (shouldApplyActiveIntentImmediately && activeIntent) {
                      onDateClick?.(dateStr, activeIntent);
                    }
                  }}
                >
                  {hasDisplayEntry && (
                    <span
                      className="w_selectCalendar_shiftChip gap-1.5"
                      style={{
                        backgroundColor: displayShiftTintColor,
                      }}
                    >
                      <span
                        translate="no"
                        className="flex size-5 shrink-0 items-center justify-center rounded-sm text-[11px] font-bold"
                        style={{
                          backgroundColor: displayShiftColor,
                          color: displayShiftTextColor,
                        }}
                      >
                        {displayShiftCode}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-text-main2">
                        {displayShiftLabel}
                      </span>
                      {showRequestStatus && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              role="img"
                              aria-label={
                                hasAvoidShiftMarker
                                  ? "피하고 싶은 근무"
                                  : "희망 근무"
                              }
                              className={`pointer-events-auto flex size-5 shrink-0 items-center justify-center ${hasAvoidShiftMarker ? "text-amber-700" : "text-sig"}`}
                            >
                              {hasAvoidShiftMarker ? (
                                <Ban aria-hidden="true" className="size-3" />
                              ) : (
                                <Check aria-hidden="true" className="size-3" />
                              )}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" sideOffset={4}>
                            {hasAvoidShiftMarker
                              ? "피하고 싶은 근무"
                              : "희망 근무"}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="bottom"
                align="start"
                sideOffset={-56}
                collisionPadding={12}
                className="!w-[320px] max-w-[calc(100vw-2rem)] !p-1.5"
                aria-label={`${dateContextLabel} 근무 신청 유형 선택`}
              >
                <div className="flex w-full flex-col gap-1">
                  <div className="flex w-full items-center justify-between border-b border-border px-1 pb-1.5">
                    <span className="text-xs font-semibold text-text-main2">
                      {dateContextLabel}
                    </span>
                    <button
                      type="button"
                      aria-label="근무 신청 팝오버 닫기"
                      title="닫기"
                      className="flex size-6 items-center justify-center rounded-sm text-text-sub transition-colors hover:bg-light-muted hover:text-text-main2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-text-sub/40"
                      onClick={() => setIsIntentPopoverOpen(false)}
                    >
                      <X aria-hidden="true" className="size-3.5" />
                    </button>
                  </div>
                  <div className="flex min-h-8 items-center gap-1">
                    <div className="flex min-w-0 max-w-[104px] flex-1 items-center gap-1.5 rounded-sm bg-light-muted px-2 text-xs">
                      <span
                        translate="no"
                        className="flex size-5 shrink-0 items-center justify-center rounded-sm text-[10px] font-bold"
                        style={{
                          backgroundColor: popoverShiftColor,
                          color: popoverShiftTextColor,
                        }}
                      >
                        {popoverShiftCode}
                      </span>
                      <span className="truncate font-semibold text-text-main2">
                        {popoverShiftDefinition?.name ?? popoverShiftCode}
                      </span>
                    </div>
                    {popoverShiftCode ? (
                      <button
                        type="button"
                        aria-pressed={currentIntent === "wanted"}
                        className={`flex min-h-8 shrink-0 items-center justify-center gap-1 rounded-sm px-1.5 text-[11px] font-semibold transition-colors hover:bg-sig/10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-text-sub/40 ${currentIntent === "wanted" ? "bg-sig/10 text-sig" : "text-sig"}`}
                        onClick={() => handleApplyIntent("wanted")}
                      >
                        <Check aria-hidden="true" className="size-3.5" />
                        희망
                      </button>
                    ) : null}
                    {popoverShiftCode ? (
                      <button
                        type="button"
                        aria-pressed={currentIntent === "avoid"}
                        className={`flex min-h-8 shrink-0 items-center justify-center gap-1 rounded-sm px-1.5 text-[11px] font-semibold text-amber-800 transition-colors hover:bg-sig/10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-text-sub/40 ${currentIntent === "avoid" ? "bg-sig/10" : ""}`}
                        onClick={() => handleApplyIntent("avoid")}
                      >
                        <Ban aria-hidden="true" className="size-3.5" />
                        피함
                      </button>
                    ) : null}
                    <button
                      type="button"
                      disabled={!currentIntent}
                      className="flex min-h-8 shrink-0 items-center justify-center gap-1 rounded-sm px-1.5 text-[11px] font-medium text-text-sub transition-colors hover:bg-light-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-text-sub/40 disabled:cursor-not-allowed disabled:opacity-40"
                      onClick={handleClearDate}
                    >
                      <Eraser aria-hidden="true" className="size-3.5" />
                      비우기
                    </button>
                  </div>
                  {Boolean(currentIntent) &&
                    submittedStatus === "requested" &&
                    !isWeeklyOff &&
                    enableCommentInput && (
                      <div className="w-full border-t border-border pt-1.5">
                        <CommentInput
                          commentValue={commentValue}
                          hasSavedComment={hasSavedCommentValue}
                          showSave
                          onCommentChange={setCommentValue}
                          onSave={handleSaveComment}
                          onDelete={handleDelete}
                          onClose={() => setIsIntentPopoverOpen(false)}
                        />
                      </div>
                    )}
                </div>
              </PopoverContent>
            </Popover>
            {/* 오른쪽 하단 카운트 표시 - 주휴, 근무코드가 존재하면 카운트는 안보이게 */}
            {shift?.type !== "주" &&
              maxCount !== undefined &&
              !shift?.comment &&
              !shift?.type && (
                <div className="absolute bottom-[2px] right-[4px] text-[#D6D6DE] select-none pointer-events-none">
                  <span className="font-pretendard font-medium text-[20px]">
                    {currentCount}
                  </span>
                  <span className="font-pretendard font-medium text-[12px]">
                    /{maxCount}
                  </span>
                </div>
              )}
          </>
        ) : (
          <div className="w_selectCalendar_shiftButton w_selectCalendar_shiftButton--disabled"></div>
        )}

        {(shift || hasAvoidShiftMarker) &&
          submittedStatus === "requested" &&
          !isWeeklyOff &&
          enableCommentInput &&
          hasSavedCommentValue && (
            <button
              type="button"
              aria-label={`${dateStr} 사유 메모 수정`}
              title={currentComment ?? undefined}
              className={`absolute left-2 right-2 top-[68px] z-10 flex items-start gap-1 text-left text-[11px] leading-4 font-medium text-text-sub underline-offset-2 transition-opacity hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig focus-visible:ring-offset-1 ${isFilterInactive ? "opacity-20" : ""}`}
              onClick={() => setIsIntentPopoverOpen(true)}
            >
              <MessageCircle
                aria-hidden="true"
                className="mt-0.5 size-3 shrink-0 text-text-sub"
              />
              <span className="line-clamp-2">{currentComment}</span>
            </button>
          )}
      </div>
    </div>
  );
}
export default Card_date;
